import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '@/supabaseClient'

const AuthContext = createContext(undefined)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [attendant, setAttendant] = useState(null)
  const [loadingAuth, setLoadingAuth] = useState(true)

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null)
      setAttendant(null)
      return
    }

    // 1) Tenta como owner (user id == profile id)
    const { data: ownerProfile, error: ownerError } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('id', userId)
      .maybeSingle()

    if (ownerError) {
      console.error('[AuthContext] profiles:', ownerError)
      setProfile(null)
      setAttendant(null)
      return
    }

    if (ownerProfile) {
      setProfile(ownerProfile)
      setAttendant(null)
      return
    }

    // 2) Fallback: login de atendente (user id == attendants.id)
    const { data: attendantData, error: attendantError } = await supabase
      .from('attendants')
      .select('id, name, email, profile_id, is_active')
      .eq('id', userId)
      .maybeSingle()

    if (attendantError) {
      console.error('[AuthContext] attendants:', attendantError)
      setProfile(null)
      setAttendant(null)
      return
    }

    if (!attendantData?.profile_id) {
      setProfile(null)
      setAttendant(null)
      return
    }

    setAttendant(attendantData)

    const { data: parentProfile, error: parentError } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('id', attendantData.profile_id)
      .maybeSingle()

    if (parentError) {
      console.error('[AuthContext] parent profile:', parentError)
      setProfile(null)
      return
    }

    setProfile(parentProfile ?? null)
  }, [])

  useEffect(() => {
    let cancelled = false

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (cancelled) return
      setSession(s ?? null)
      setUser(s?.user ?? null)
      if (s?.user?.id) loadProfile(s.user.id)
      else setProfile(null)
      setLoadingAuth(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s ?? null)
      setUser(s?.user ?? null)
      if (s?.user?.id) loadProfile(s.user.id)
      else setProfile(null)
      setLoadingAuth(false)
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [loadProfile])

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }, [])

  const signUp = useCallback(async ({ fullName, email, password }) => {
    const safeName = String(fullName || '').trim()
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: safeName,
          name: safeName,
        },
      },
    })
    if (error) throw error
    return data
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }, [])

  const getProfileId = useCallback(() => attendant?.profile_id ?? profile?.id ?? null, [attendant, profile])

  const value = useMemo(
    () => ({
      session,
      user,
      profile,
      attendant,
      loadingAuth,
      signIn,
      signUp,
      signOut,
      getProfileId,
    }),
    [session, user, profile, attendant, loadingAuth, signIn, signUp, signOut, getProfileId]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider')
  }
  return ctx
}
